import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getHomework, getStudentAnswers, submitHomework } from "../api/client";

export default function Homework() {
  const { classId } = useParams<{ classId: string }>();
  const [data, setData] = useState<any>(null);
  const [studentId, setStudentId] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [savedAnswers, setSavedAnswers] = useState<Record<string, { answer_text: string; submitted_at: string }>>({});
  const [saving, setSaving] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState<any>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!classId) return;
    getHomework(classId).then((r) => setData(r.data)).catch(() => setError("Class not found"));
  }, [classId]);

  useEffect(() => {
    if (!classId || !studentId) return;
    getStudentAnswers(classId, studentId).then((r) => {
      setSavedAnswers(r.data);
      const prefill: Record<string, string> = {};
      for (const [qid, val] of Object.entries(r.data)) {
        prefill[qid] = val.answer_text;
      }
      setAnswers(prefill);
    });
  }, [classId, studentId]);

  const handleSubmitAssignment = async () => {
    if (!classId || !studentId || !activeAssignment) return;
    const questionAnswers = activeAssignment.questions
      .filter((q: any) => answers[q.id]?.trim())
      .map((q: any) => ({ question_id: q.id, answer_text: answers[q.id] }));
    if (questionAnswers.length === 0) return;
    setSaving(true);
    try {
      await submitHomework(classId, { student_id: studentId, answers: questionAnswers });
      setSubmitted(true);
      // Refresh saved answers
      getStudentAnswers(classId, studentId).then((r) => setSavedAnswers(r.data));
    } catch (e) {
      alert("Failed to save. Check your connection.");
    }
    setSaving(false);
  };

  const handleSaveProgress = async () => {
    if (!classId || !studentId || !activeAssignment) return;
    const questionAnswers = activeAssignment.questions
      .filter((q: any) => answers[q.id]?.trim())
      .map((q: any) => ({ question_id: q.id, answer_text: answers[q.id] }));
    if (questionAnswers.length === 0) return;
    setSaving(true);
    try {
      await submitHomework(classId, { student_id: studentId, answers: questionAnswers });
      getStudentAnswers(classId, studentId).then((r) => setSavedAnswers(r.data));
    } catch (e) {
      alert("Failed to save.");
    }
    setSaving(false);
  };

  const getAnsweredCount = (assignment: any) => {
    return assignment.questions.filter((q: any) => savedAnswers[q.id]).length;
  };

  // --- Error / Loading ---
  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: "center", color: "#78716c" }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", marginBottom: 8 }}>Not Found</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", color: "#78716c" }}>
      Loading...
    </div>
  );

  // --- Submitted success ---
  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400, padding: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>&#10003;</div>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 600, color: "#166534", marginBottom: 12 }}>Homework Submitted!</h1>
        <p style={{ color: "#15803d", fontSize: 15, marginBottom: 32, lineHeight: 1.6 }}>
          Your answers for <strong>{activeAssignment?.title}</strong> have been saved.
        </p>
        <button
          onClick={() => { setSubmitted(false); setActiveAssignment(null); }}
          style={{
            padding: "12px 32px", borderRadius: 8, border: "1px solid #16a34a",
            background: "#fff", color: "#166534", fontSize: 14, fontWeight: 500,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Back to Homework List
        </button>
      </div>
    </div>
  );

  const headerStyle: React.CSSProperties = { background: "#1c1917", color: "#fff", padding: "24px 0" };
  const containerStyle: React.CSSProperties = { maxWidth: 700, margin: "0 auto", padding: "0 20px" };

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f5", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={containerStyle}>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600 }}>Homework Portal</h1>
        </div>
      </div>

      <div style={{ ...containerStyle, padding: "24px 20px" }}>
        {/* Student name input */}
        {!studentId ? (
          <div style={{ background: "#fff", borderRadius: 14, padding: 32, textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, marginBottom: 16 }}>Enter your name</h2>
            <p style={{ color: "#78716c", fontSize: 14, marginBottom: 20 }}>Type your name to access homework</p>
            <div style={{ maxWidth: 350, margin: "0 auto" }}>
              <input
                list="student-names"
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value);
                  const match = data.students.find((s: any) => s.name.toLowerCase() === e.target.value.toLowerCase());
                  if (match) setStudentId(match.id);
                }}
                placeholder="Start typing your name..."
                style={{
                  width: "100%", padding: "14px 16px", borderRadius: 10,
                  border: "1px solid #e7e5e4", fontSize: 16, fontFamily: "inherit",
                  textAlign: "center",
                }}
                autoFocus
              />
              <datalist id="student-names">
                {data.students.map((s: any) => (
                  <option key={s.id} value={s.name} />
                ))}
              </datalist>
            </div>
          </div>
        ) : !activeAssignment ? (
          /* --- Assignment list --- */
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <span style={{ fontSize: 13, color: "#78716c" }}>Logged in as</span>
                <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20 }}>
                  {data.students.find((s: any) => s.id === studentId)?.name}
                </h2>
              </div>
              <button
                onClick={() => { setStudentId(""); setNameInput(""); setAnswers({}); setSavedAnswers({}); }}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e7e5e4", background: "#fff", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >Switch</button>
            </div>

            {data.assignments.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: 48, textAlign: "center", color: "#a8a29e" }}>
                No homework assigned yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {data.assignments.map((a: any) => {
                  const answered = getAnsweredCount(a);
                  const total = a.questions.length;
                  const isComplete = total > 0 && answered === total;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setActiveAssignment(a)}
                      style={{
                        background: "#fff", borderRadius: 14, padding: "20px 24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: isComplete ? "2px solid #16a34a" : "1px solid #e7e5e4",
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <div>
                        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{a.title}</h3>
                        <div style={{ fontSize: 13, color: "#78716c" }}>
                          {total} question{total !== 1 ? "s" : ""}
                          {a.due_date && <span> &middot; Due: {a.due_date}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {isComplete ? (
                          <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                            Completed
                          </span>
                        ) : answered > 0 ? (
                          <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                            {answered}/{total}
                          </span>
                        ) : (
                          <span style={{ background: "#f5f5f4", color: "#78716c", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                            Not started
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          /* --- Single assignment view --- */
          <>
            <button
              onClick={() => setActiveAssignment(null)}
              style={{ background: "none", border: "none", color: "#78716c", fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: 16, padding: 0 }}
            >
              &larr; Back to homework list
            </button>

            <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600 }}>{activeAssignment.title}</h3>
                  {activeAssignment.description && <p style={{ color: "#78716c", fontSize: 13, marginTop: 4 }}>{activeAssignment.description}</p>}
                </div>
                {activeAssignment.due_date && (
                  <span style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: "4px 10px", borderRadius: 6, fontWeight: 500, whiteSpace: "nowrap" }}>
                    Due: {activeAssignment.due_date}
                  </span>
                )}
              </div>

              {activeAssignment.questions.map((q: any, i: number) => {
                const isSaved = savedAnswers[q.id]?.answer_text === answers[q.id];
                return (
                  <div key={q.id} style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1c1917", marginBottom: 8 }}>
                      Q{i + 1}. {q.question_text}
                    </label>
                    <textarea
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      placeholder="Type your answer..."
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 8,
                        border: `1px solid ${isSaved && answers[q.id] ? "#16a34a" : "#e7e5e4"}`,
                        fontSize: 14, fontFamily: "inherit", minHeight: 80, resize: "vertical",
                        background: isSaved && answers[q.id] ? "#f0fdf4" : "#fff",
                      }}
                    />
                    {savedAnswers[q.id] && (
                      <span style={{ fontSize: 11, color: "#16a34a", marginTop: 4, display: "inline-block" }}>
                        Saved {new Date(savedAnswers[q.id].submitted_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                );
              })}

              <div style={{ display: "flex", gap: 12, paddingTop: 16, borderTop: "1px solid #e7e5e4" }}>
                <button
                  onClick={handleSaveProgress}
                  disabled={saving}
                  style={{
                    padding: "10px 24px", borderRadius: 8, border: "1px solid #e7e5e4",
                    background: "#fff", color: "#1c1917", fontSize: 14, fontWeight: 500,
                    cursor: saving ? "wait" : "pointer", fontFamily: "inherit",
                  }}
                >
                  Save Progress
                </button>
                <button
                  onClick={handleSubmitAssignment}
                  disabled={saving}
                  style={{
                    padding: "10px 32px", borderRadius: 8, border: "none",
                    background: "#0d9488", color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: saving ? "wait" : "pointer", fontFamily: "inherit",
                    boxShadow: "0 2px 8px rgba(13,148,136,0.35)", flex: 1,
                  }}
                >
                  {saving ? "Submitting..." : "Submit Answers"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
